import React, { useState } from 'react';
import { FiMicOff, FiMessageSquare, FiX } from 'react-icons/fi';
import '../broadcastNext.css';

// The design gives each participant a different avatar colour; pick one deterministically
// so a person keeps the same colour across renders and reloads.
const AVATAR_COLORS = [
  '#594099',
  '#7255c2',
  '#2f8f6b',
  '#b06a2c',
  '#c2456f',
  '#2b7fc4',
  '#8a7a2a',
  '#4a5bb8',
];

const colorFor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// The privilege ladder, most senior first. Someone holding several is named by the one that
// outranks the rest, and "superadmin" contains "admin", so the order is what keeps a super
// admin from being labelled a plain one. Spellings follow the rest of the app (see
// AdministratorCard and userProfile): the stored value is "SuperAdmin", shown "Super Admin".
const PRIVILEGE_LABELS = [
  ['superadmin', 'Super Admin'],
  ['admin', 'Admin'],
  ['teacher', 'Teacher'],
  ['moderator', 'Moderator'],
];

/**
 * What to call someone in the participants list, decided by privileges rather than by what
 * a row happens to be missing. These rows used to say "Co-host" for everyone who was not a
 * student, which told a super admin nothing about their own standing and was the same word
 * for an admin, a teacher and a moderator. Name the privilege instead.
 *
 * Privileges reach us for remote participants too - ChatClient sends them at join and the
 * room echoes them back on the user list - so this works for the whole roster, not only for
 * the signed-in user. "Student" is the fallback when nothing elevated is found, never a
 * pre-emptive answer.
 *
 * Shared so there is one rule and not three: this had already drifted between the lobby and
 * the class, which is how the same person could be read differently on each screen.
 */
export const roleOf = (user = {}) => {
  const bag = []
    .concat(user?.access || [], user?.role || [], user?.privilage || [], user?.privilege || [])
    .join(',')
    .toLowerCase();
  const ranked = PRIVILEGE_LABELS.find(([token]) => bag.includes(token));
  if (ranked) return ranked[1];
  // The scheduled host, when the payload said nothing about privileges.
  if (user?.isTeacher) return 'Teacher';
  return 'Student';
};

export const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'U';

// mediasoup reports a score only for people who are actually producing, so most rows have
// nothing to show — a listening student has no uplink to rate. Absent bars mean "not
// publishing", which is why nothing is drawn rather than an empty meter.
const SignalBars = ({ bars, score }) => (
  <span
    className={`bn-person-signal ${bars <= 2 ? 'bn-weak' : bars === 3 ? 'bn-fair' : ''}`}
    title={`Connection ${score.toFixed(0)}/10${bars <= 2 ? ' — losing packets' : ''}`}
  >
    {[0, 1, 2, 3, 4].map((index) => (
      <i key={index} className={index < bars ? 'bn-on' : ''} style={{ height: 3 + index * 2 }} />
    ))}
  </span>
);

const PersonRow = ({ person, present, onMessage, signal }) => (
  <div className={`bn-person ${present ? '' : 'bn-idle'}`}>
    <div className="bn-person-avatar-wrap">
      {/* Coloured whether or not they are here. Absence is already said twice over - the red
          presence dot and the dimmed row - and draining the avatar to grey as well turned the
          absent half of the list into a column of identical blanks. The colour is per person,
          so it is also what makes a name findable in a long roster at a glance. */}
      <div
        className="bn-person-avatar"
        style={{ background: colorFor(person.id || person.name) }}
      >
        {person.image ? <img src={person.image} alt="" /> : initialsOf(person.name)}
      </div>
      <span className={`bn-presence ${present ? '' : 'bn-idle'}`} />
    </div>

    <div className="bn-person-main">
      <div className="bn-person-name">
        {person.name}
        {person.isSelf ? ' (You)' : ''}
      </div>
      <div className="bn-person-role">{person.role}</div>
    </div>

    {present && signal ? <SignalBars bars={signal.bars} score={signal.score} /> : null}

    {present && person.muted ? (
      <span className="bn-mic-off" title="Muted">
        <FiMicOff size={16} />
      </span>
    ) : null}

    {onMessage && !person.isSelf ? (
      <button
        type="button"
        className="bn-row-btn"
        onClick={() => onMessage(person)}
        title={`Message ${person.name}`}
      >
        <FiMessageSquare size={16} />
      </button>
    ) : null}
  </div>
);

const Section = ({ label, count, tone, children }) => (
  <>
    <div className="bn-section-head">
      <span className={`bn-dot ${tone === 'absent' ? 'bn-dot-idle' : ''}`} />
      <span className="bn-section-label">{label}</span>
      <span className="bn-section-count">· {count}</span>
    </div>
    <div className="bn-people">{children}</div>
  </>
);

/**
 * Right-hand side panel: who is present, who is absent.
 * Present/absent comes from the room's `statusToShow` — the same signal the old
 * UsersList used for its green/red dot — so no new data source.
 */
const ParticipantsPanel = ({
  present = [],
  absent = [],
  onClose,
  onMessage,
  chatSlot = null,
  chatNote = 'Chat opens when the class starts.',
  activeTab,
  onTabChange,
  // Group plus private, for the Chat tab's dot. The tab is the only sight of the chat
  // while the roster is up, so without this a message that arrives now is invisible until
  // someone thinks to look.
  chatUnread = 0,
  // userId -> {bars, score}, from mediasoup's producerScore. Only publishers appear.
  signals = {},
}) => {
  const [ownTab, setOwnTab] = useState('participants');
  // Controlled when the caller owns the tab (in class it mirrors redux's rightViewType),
  // self-managed otherwise.
  const tab = activeTab ?? ownTab;
  const setTab = (next) => {
    if (onTabChange) {
      onTabChange(next);
      return;
    }
    setOwnTab(next);
  };

  return (
    <div className="bn-panel">
      <div className="bn-tabs">
        <button
          type="button"
          className={`bn-tab ${tab === 'participants' ? 'bn-on' : ''}`}
          onClick={() => setTab('participants')}
        >
          Participants
        </button>
        <button
          type="button"
          className={`bn-tab ${tab === 'chat' ? 'bn-on' : ''}`}
          onClick={() => setTab('chat')}
        >
          Chat
          {tab !== 'chat' && chatUnread > 0 ? <span className="bn-unread-dot" /> : null}
        </button>
        <span className="bn-tab-spacer" />
        <button type="button" className="bn-tab-close" onClick={onClose} title="Close panel">
          <FiX size={18} />
        </button>
      </div>

      {tab === 'participants' ? (
        <div className="bn-panel-scroll">
          <Section label="Present" count={present.length} tone="present">
            {present.length ? (
              present.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  present
                  onMessage={onMessage}
                  signal={signals[person.id]}
                />
              ))
            ) : (
              <div className="bn-empty">Nobody has joined yet.</div>
            )}
          </Section>

          {absent.length ? (
            <Section label="Absent" count={absent.length} tone="absent">
              {absent.map((person) => (
                <PersonRow key={person.id} person={person} present={false} onMessage={onMessage} />
              ))}
            </Section>
          ) : null}
        </div>
      ) : (
        chatSlot || <div className="bn-panel-scroll"><div className="bn-empty">{chatNote}</div></div>
      )}
    </div>
  );
};

export default ParticipantsPanel;
